import { DEFAULT_TEMPLATES, labelDocuments } from '@iws/pos-devices'
import { CreditCard, Mail, Phone, Printer, User } from 'lucide-react'
import { Control, FieldErrors, UseFormWatch } from 'react-hook-form'
import { TextInput, SwitchInput } from '@/components/atoms'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useBusiness } from '@/contexts/BusinessContext'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { printSheet } from '@/lib/printSheet'
import { UserFormState } from './userFieldsConfig'
import { UserImageSection } from './UserImageSection'

interface UserProfileTabProps {
    mode: 'create' | 'edit' | 'view'
    control: Control<UserFormState>
    watch: UseFormWatch<UserFormState>
    errors: FieldErrors<UserFormState>
    fields: any
    roleOptions: { label: string; value: string }[]
    rolesLoading: boolean
    onSelectImage: () => void
    onRemoveImage: () => void
    onRoleChange: (values: string[]) => void
    onNewCard: () => void
}

export const UserProfileTab = ({
    mode,
    control,
    watch,
    errors,
    fields,
    roleOptions,
    rolesLoading,
    onSelectImage,
    onRemoveImage,
    onRoleChange,
    onNewCard,
}: UserProfileTabProps) => {
    const formValues = watch()
    const { profile } = useBusiness()
    const hasPos = useFeatureFlags().isFeatureEnabled('pos')

    // One card on an A4 card sheet through the browser (POS.md §18.9); the till's lock screen scans it.
    const printCard = () => {
        const data = { shop: { name: profile?.name ?? '' }, staff: { name: formValues.name, barcode: formValues.barcode } }
        const [doc] = labelDocuments(DEFAULT_TEMPLATES.staff_card, [{ data, copies: 1 }], 'cards_2x5')
        if (doc) printSheet(doc, `${formValues.name} card`)
    }

    return (
        <div className="space-y-6">
            <UserImageSection
                imageUrl={formValues.image_url_temp}
                mode={mode}
                onSelectClick={onSelectImage}
                onRemoveClick={onRemoveImage}
            />

            <div className="space-y-4">
                <TextInput
                    control={control}
                    name="name"
                    label="Full name"
                    placeholder="John Doe"
                    disabled={mode === 'view'}
                    rules={fields.name.rules}
                    errors={errors}
                    icon={<User className="size-4" />}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <TextInput
                        control={control}
                        name="email"
                        label="Email"
                        placeholder="john@example.com"
                        disabled={mode === 'view'}
                        rules={fields.email.rules}
                        errors={errors}
                        icon={<Mail className="size-4" />}
                    />

                    <TextInput
                        control={control}
                        name="phone"
                        label="Phone"
                        placeholder="017XXXXXXXX"
                        disabled={mode === 'view'}
                        errors={errors}
                        icon={<Phone className="size-4" />}
                    />
                </div>

                {hasPos && (
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <TextInput
                                control={control}
                                name="barcode"
                                label="Staff card (POS)"
                                placeholder="Scan a card or make a new one"
                                disabled={mode === 'view'}
                                errors={errors}
                                icon={<CreditCard className="size-4" />}
                            />
                        </div>
                        {mode !== 'view' && (
                            <Button type="button" variant="secondary" onClick={onNewCard}>
                                New card
                            </Button>
                        )}
                        {formValues.barcode && (
                            <Button type="button" variant="outline" onClick={printCard}>
                                <Printer />
                                Print
                            </Button>
                        )}
                    </div>
                )}

                {mode === 'create' && (
                    <TextInput
                        control={control}
                        name="password"
                        label="Password"
                        type="password"
                        placeholder="••••••••"
                        rules={{ required: 'Password is required' }}
                        errors={errors}
                    />
                )}

                <div className="space-y-2">
                    <Label className="text-sm font-normal text-ink">Roles</Label>
                    <MultiSelect
                        options={roleOptions}
                        value={watch('role_ids') || []}
                        onChange={onRoleChange}
                        placeholder={rolesLoading ? 'Loading roles...' : 'Select roles'}
                        disabled={mode === 'view' || rolesLoading}
                    />
                    {errors.role_ids && (
                        <p className="text-sm text-error">
                            {errors.role_ids.message}
                        </p>
                    )}
                    <p className="text-sm text-text-2">
                        Permissions come from these roles unless you override them on the Permissions tab.
                    </p>
                </div>

                <SwitchInput
                    control={control}
                    name="status"
                    label="Account status"
                    description={
                        formValues.status === 'active'
                            ? 'User can access the dashboard.'
                            : 'User access is currently disabled.'
                    }
                    disabled={mode === 'view'}
                    activeValue="active"
                    inactiveValue="inactive"
                />
            </div>
        </div>
    )
}
